import { AnimatePresence, motion } from 'framer-motion';
import { type FC, useEffect, useRef, useState } from 'react';

import type { TabItem } from '@/types';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useBreakpoint from '@/hooks/use-break-point';
import { cn } from '@/lib/utils';
import isLandscape from '@/utils/helpers/is-landscape';

interface RTabsProps {
  value?: string;
  defaultValue?: string;
  tabItems: TabItem[];
  className?: string;
  tabsClassName?: string;
  tabListClassName?: string;
  tabTriggerClassName?: string;
  tabContentClassName?: string;
  resetKey?: string;
  onValueChange?: (value: string) => void;
}

const RTabs: FC<RTabsProps> = ({
  tabItems,
  className,
  tabsClassName,
  tabListClassName,
  tabTriggerClassName,
  tabContentClassName,
  defaultValue,
  resetKey,
  value,
  onValueChange,
}) => {
  const isLaptop: boolean = useBreakpoint('laptop');
  const isControlled = value !== undefined;
  const initialTab = defaultValue ?? tabItems[0]?.value;
  const [internalValue, setInternalValue] = useState(initialTab);

  const currentValue = isControlled ? value : internalValue;

  const handleValueChange = (newValue: string) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(orientation: landscape)');

    const updateOrientation = () => {
      setIsMobileLandscape(isLaptop && isLandscape());
    };

    updateOrientation();
    mediaQuery.addEventListener('change', updateOrientation);

    return () => mediaQuery.removeEventListener('change', updateOrientation);
  }, []);

  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  return (
    <div className={cn('flex flex-col flex-1 min-h-0 w-full', className)}>
      <Tabs
        value={currentValue}
        onValueChange={handleValueChange}
        key={resetKey}
        className={cn('flex flex-col flex-1 min-h-0 w-full gap-0', tabsClassName)}
      >
        <div
          className={cn('shrink-0 bg-background mx-4 border-b', {
            'sticky top-0 z-10': !isMobileLandscape,
          })}
        >
          <TabsList className={cn('bg-transparent pl-0 pb-0', tabListClassName)}>
            {tabItems.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className={cn(
                  'cursor-pointer text-muted-foreground rounded-none bg-background h-full data-[state=active]:cursor-default data-[state=active]:text-foreground data-[state=active]:z-5 data-[state=active]:shadow-none border-0 border-b-2 border-transparent data-[state=active]:border-primary',
                  tabTriggerClassName
                )}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <div className="flex-1 min-h-0 w-full overflow-hidden" ref={scrollWrapperRef}>
          <ScrollArea className="h-full w-full">
            {isMobileLandscape}
            <AnimatePresence mode="wait" initial={false}>
              {tabItems.map(
                (item) =>
                  currentValue === item.value && (
                    <TabsContent
                      key={item.value}
                      value={item.value}
                      forceMount
                      className={cn(
                        'm-0 w-full outline-none data-[state=inactive]:hidden',
                        tabContentClassName
                      )}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="w-full px-4"
                      >
                        {item.content}
                      </motion.div>
                    </TabsContent>
                  )
              )}
            </AnimatePresence>
          </ScrollArea>
        </div>
        {tabItems.find((item) => item.value === currentValue)?.footer}
      </Tabs>
    </div>
  );
};

export default RTabs;
